import { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAnalyticsBackState } from "./hooks/useAnalyticsBack";
import { AlertCard, Spinner } from "@libertify/ui";
import { FeatureFlags } from "@/constants";
import { useFeature } from "@/hooks/useFeature";
import { Col, Empty, Flex, Row, Typography } from "antd";
import { sum } from "lodash";
import type { AddonsSummary } from "types/";
import { AnalyticsSubPageLayout } from "../components/analytics/AnalyticsSubPageLayout";
import { CompactStatCard } from "../components/analytics/CompactStatCard";
import { AllVersionsTag } from "../components/analytics/AllVersionsTag";
import { showsOneVersion } from "../components/analytics/utils/";
import {
  AddonPills,
  OVERVIEW_PILL,
  VIDEO_PILL,
} from "../components/analytics/addons/AddonPills";
import { AddonsOverviewPanel } from "../components/analytics/addons/AddonsOverviewPanel";
import { FlashcardsPanel } from "../components/analytics/addons/FlashcardsPanel";
import { QuizPanel } from "../components/analytics/addons/QuizPanel";
import { StandoutsPanel } from "../components/analytics/addons/StandoutsPanel";
import { SummaryPanel } from "../components/analytics/addons/SummaryPanel";
import { VideoPanel } from "../components/analytics/addons/VideoPanel";
import {
  visibleAddons,
  type AddonKey,
} from "../components/analytics/addons/utils/metrics";
import {
  useAddonDetail,
  useProjectAnalyticsTabs,
  useQuizPreparation,
} from "../components/analytics/addons/hooks/useAddonsAnalytics";
import { useAnalyticsPage } from "./hooks/useAnalyticsPage";

const T_PREFIX = "media.projects.analytics.addons";

// The Video page redirects here with the Video pill to open.
const initialPill = (state: unknown) =>
  typeof state === "object" &&
  state !== null &&
  "addonPill" in state &&
  state.addonPill === VIDEO_PILL
    ? VIDEO_PILL
    : OVERVIEW_PILL;

export const AddonsAnalyticsPage = () => {
  // The add-on endpoints take no date filter.
  const page = useAnalyticsPage<AddonsSummary>({
    analyticsType: "addons",
    dateFiltered: false,
    versioned: true,
  });
  const { search, state } = useLocation();
  const [selected, setSelected] = useState<string>(() => initialPill(state));
  const backState = useAnalyticsBackState();
  const { summaryData: summary, isDataLoading, projectId, t } = page;
  const version = page.version?.requested;
  // The add-ons summary takes no version: its cards count every version.
  const summaryTag = <AllVersionsTag hidden={!showsOneVersion(page.version)} />;

  const addons = summary ? visibleAddons(summary.addons) : [];
  // E.g. a Video + Chat project: the video gets a tab of its own instead. A
  // project with add-ons but no analytics yet stays here, on "No data".
  const tabs = useProjectAnalyticsTabs(projectId, true);
  const noAddons = tabs?.hasAddons === false;
  const hasVideo = !!tabs?.hasVideo;
  // No video, no pill: the overview instead.
  const videoSelected = hasVideo && selected === VIDEO_PILL;
  // A pill can disappear when the summary refetches: fall back to the overview.
  const active = addons.find((a) => a.addon === selected);

  const isVisible = (key: AddonKey) =>
    !!projectId && addons.some((a) => a.addon === key);
  // Every tab loads in the background as soon as the summary is known, so
  // switching pills never waits on Athena.
  const details: Record<AddonKey, ReturnType<typeof useAddonDetail>> = {
    quiz: useAddonDetail(projectId, "quiz", version, isVisible("quiz")),
    summary: useAddonDetail(
      projectId,
      "summary",
      version,
      isVisible("summary")
    ),
    flashcards: useAddonDetail(
      projectId,
      "flashcards",
      version,
      isVisible("flashcards")
    ),
    standouts: useAddonDetail(
      projectId,
      "standouts",
      version,
      isVisible("standouts")
    ),
  };
  const { preparation, preparationLoading, preparationFailed } =
    useQuizPreparation(projectId, version, isVisible("quiz"));
  const activeDetail = active ? details[active.addon] : undefined;
  const detail = activeDetail?.data?.data ?? null;
  const detailLoading = activeDetail?.isInitialLoading ?? false;

  const quiz = addons.find((a) => a.addon === "quiz");
  // No quiz, no quiz score: the card would only ever read "--".
  const showQuizKpi = quiz != null || !summary;
  const kpiSpan = showQuizKpi ? 8 : 12;
  const knownSeconds = addons.flatMap((a) =>
    a.active_seconds == null ? [] : [a.active_seconds]
  );
  // An unknown time renders as a dash, never 0s.
  const totalSeconds = knownSeconds.length > 0 ? sum(knownSeconds) : null;

  // Athena queries take a few seconds: say so instead of leaving a blank area.
  const loading = (
    <Flex vertical align="center" gap={12} style={{ padding: 48 }}>
      <Spinner size="lg" />
      <Typography.Text type="secondary">
        {t(`${T_PREFIX}.loading`)}
      </Typography.Text>
    </Flex>
  );

  const renderPanel = () => {
    // The video follows the date range, so its panel carries the picker.
    if (videoSelected)
      return (
        <VideoPanel
          projectId={projectId}
          project={page.project}
          dateRange={page.dateRange}
          setDateRange={page.setDateRange}
          version={page.version}
        />
      );
    if (!summary) return isDataLoading ? loading : null;
    if (!active)
      return <AddonsOverviewPanel summary={summary} badge={summaryTag} />;
    // The quiz panel waits for the preparation too, or it would briefly claim
    // that nobody finished the quiz. The other panels only use it for their
    // quiz effect card, which shows up once the preparation lands.
    if (detailLoading || (active.addon === "quiz" && preparationLoading))
      return loading;
    // The backend returns no detail for an add-on without item nor step.
    if (!detail)
      return activeDetail?.isError ? (
        <AlertCard type="error" title={t(`${T_PREFIX}.loadError`)} />
      ) : (
        <Empty description={t(`${T_PREFIX}.empty.noDetail`)} />
      );
    const panelProps = {
      activation: active,
      detail,
      // Without a quiz there is nothing to compare against.
      preparation: quiz ? preparation : null,
      preparationFailed: !!quiz && preparationFailed,
      projectSessions: summary.project_sessions,
    };
    switch (active.addon) {
      case "quiz":
        return (
          <QuizPanel
            activation={active}
            detail={detail}
            preparation={preparation}
            preparationFailed={preparationFailed}
            projectSessions={summary.project_sessions}
          />
        );
      case "summary":
        return <SummaryPanel {...panelProps} />;
      case "flashcards":
        return <FlashcardsPanel {...panelProps} />;
      case "standouts":
        return <StandoutsPanel {...panelProps} />;
    }
  };

  if (noAddons) {
    return (
      <Navigate
        to={{ pathname: "../video-explainer", search }}
        relative="path"
        replace
        state={backState}
      />
    );
  }

  return (
    <AnalyticsSubPageLayout
      authKey="addonsAnalytics"
      translationPrefix={T_PREFIX}
      page={page}
      // The video doesn't need the add-ons summary: show it even if that fails.
      childrenSkipSummary={videoSelected}
      subNav={
        <AddonPills
          addons={addons}
          hasVideo={hasVideo}
          activeKey={
            videoSelected ? VIDEO_PILL : (active?.addon ?? OVERVIEW_PILL)
          }
          onChange={setSelected}
        />
      }
    >
      {/* Add-on figures: not shown under the video, which they don't describe. */}
      {videoSelected ? null : (
        <Row gutter={[8, 8]} style={{ width: "100%" }}>
          <Col xs={24} md={kpiSpan}>
            <CompactStatCard
              badge={summaryTag}
              label={t(`${T_PREFIX}.kpi.sessions`)}
              tooltip={t(`${T_PREFIX}.kpi.sessionsInfo`)}
              metric={summary?.project_sessions}
              loading={isDataLoading}
              subtitle={
                summary
                  ? t(`${T_PREFIX}.kpi.sessionsHint`, {
                      count: summary.project_viewers,
                    })
                  : undefined
              }
            />
          </Col>
          {showQuizKpi ? (
            <Col xs={24} md={kpiSpan}>
              <CompactStatCard
                badge={summaryTag}
                label={t(`${T_PREFIX}.kpi.quizScore`)}
                metric={quiz?.avg_quiz_score}
                type="percentage"
                loading={isDataLoading}
                subtitle={t(
                  quiz?.avg_quiz_score == null
                    ? `${T_PREFIX}.kpi.quizScoreEmpty`
                    : `${T_PREFIX}.kpi.quizScoreHint`
                )}
              />
            </Col>
          ) : null}
          <Col xs={24} md={kpiSpan}>
            <CompactStatCard
              badge={summaryTag}
              label={t(`${T_PREFIX}.kpi.time`)}
              metric={totalSeconds}
              type="minutes"
              loading={isDataLoading}
              subtitle={t(`${T_PREFIX}.kpi.timeHint`)}
            />
          </Col>
        </Row>
      )}
      {renderPanel()}
    </AnalyticsSubPageLayout>
  );
};

// Flag off: send a direct URL back to the overview so no add-on endpoint is called.
export const AddonsAnalyticsRoute = () => {
  const { isEnabled, isLoading } = useFeature(FeatureFlags.AddonsAnalytics);
  const backState = useAnalyticsBackState();
  if (isLoading) {
    return (
      <Flex justify="center" style={{ padding: 48 }}>
        <Spinner size="lg" />
      </Flex>
    );
  }
  return isEnabled ? (
    <AddonsAnalyticsPage />
  ) : (
    <Navigate to=".." relative="path" replace state={backState} />
  );
};
